import { useState } from "react";

import {
  Licenses,
  ManageSubscriptionDialog,
} from "common/components/subscriptions";
import { Button } from "common/components/ui/Button";
import useCustomer from "common/datahooks/useCustomer";
import useSubscriptionMutations from "common/datahooks/useSubscriptionMutations";
import { useSelectedWorkspaceContext } from "common/helpers/SelectedWorkspaceContext";
import useSubscriptionPricing from "common/hooks/useSubscriptionPricing";
import useTwBreakpoint from "common/hooks/useTwBreakpoint";
import useWorkspaceNavigate from "common/hooks/useWorkspaceNavigate";
import type { SubscriptionType } from "common/types";

import DesktopLayout from "./DesktopLayout";
import MobileLayout from "./MobileLayout";

export default function SubscriptionActivation() {
  const isDesktop = useTwBreakpoint("lg");
  const { relativeNavigate } = useWorkspaceNavigate();
  const [subscriptionType, setSubscriptionType] =
    useState<SubscriptionType>("user");
  const [isOpen, setIsOpen] = useState(false);

  const { subscription, rental_subscription: rentedSubscription } =
    useSelectedWorkspaceContext();

  const { customerInfo } = useCustomer();
  const { plan, isLoadingPricing } = useSubscriptionPricing("month", "user");

  const { createSubscription, isCreatingSubscription } =
    useSubscriptionMutations();

  const isExpired =
    subscription?.status === "cancelled" ||
    rentedSubscription?.status === "cancelled";

  const shouldActivateTrial =
    subscriptionType === "user" && !customerInfo.had_trial;

  async function startTrial() {
    await createSubscription({
      planId: plan.id,
      type: "user",
      count: 1,
      paymentSourceId: null,
    });

    relativeNavigate("onboarding?welcome-screen=true");
  }

  const startTrialButton = shouldActivateTrial && (
    <Button
      onClick={startTrial}
      isLoading={isCreatingSubscription || isLoadingPricing}
      className="mt-auto ml-auto"
      size={isDesktop ? "md" : "lg"}
    >
      Start 7-day free trial
    </Button>
  );

  const Component = isDesktop ? DesktopLayout : MobileLayout;

  return (
    <>
      <Component
        title={isExpired ? "Your subscription expired" : "Active plan required"}
        subscriptionType={subscriptionType}
        setSubscriptionType={setSubscriptionType}
        onContinue={() => setIsOpen(true)}
        startTrialButton={startTrialButton}
      />

      {subscriptionType === "user" ? (
        <Licenses isOpen={isOpen} setIsOpen={setIsOpen} />
      ) : (
        <ManageSubscriptionDialog
          plan="avatar"
          isOpen={isOpen}
          setIsOpen={setIsOpen}
          fixedPricingPeriodInfo={
            rentedSubscription
              ? {
                  period: rentedSubscription.item_price.period,
                  period_unit: rentedSubscription.item_price.period_unit,
                }
              : { period: 1, period_unit: "month" }
          }
        />
      )}
    </>
  );
}
